import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useInstall } from '../context/InstallContext';

/**
 * Slim CasaOthers-branded install banner. Renders NOTHING on browsers that
 * don't fire `beforeinstallprompt` (iOS Safari/Chrome, Firefox mobile) — the
 * `shouldShowBanner` flag stays false on those.
 *
 * Lifecycle:
 *   t=0     mount, hidden
 *   t=5s    slide down from top
 *   t=12s   slide back up (if user hasn't interacted)
 *   click Installeer  → fires native prompt via deferredPrompt
 *   click ×           → cooldown 7 days (footer link still visible)
 */
const InstallBanner = () => {
  const { shouldShowBanner, promptInstall, dismissBanner } = useInstall();
  const [phase, setPhase] = useState('hidden'); // hidden | entering | visible | leaving

  useEffect(() => {
    if (!shouldShowBanner) return undefined;
    const showT = setTimeout(() => setPhase('entering'), 5000);
    const settleT = setTimeout(() => setPhase('visible'), 5060);
    const leaveT = setTimeout(() => setPhase('leaving'), 5000 + 7000);
    const removeT = setTimeout(() => setPhase('hidden'), 5000 + 7000 + 400);
    return () => {
      clearTimeout(showT); clearTimeout(settleT);
      clearTimeout(leaveT); clearTimeout(removeT);
    };
  }, [shouldShowBanner]);

  if (phase === 'hidden') return null;

  const onInstall = async () => {
    setPhase('leaving');
    const res = await promptInstall();
    // If user rejected, still respect the 7-day cooldown to avoid nagging.
    if (res.outcome === 'dismissed') dismissBanner();
    setTimeout(() => setPhase('hidden'), 400);
  };
  const onClose = () => {
    dismissBanner();
    setPhase('leaving');
    setTimeout(() => setPhase('hidden'), 400);
  };

  return (
    <div
      role="dialog"
      aria-label="CasaOthers app installatie"
      style={{
        ...styles.wrap,
        transform: phase === 'entering' || phase === 'leaving' ? 'translateY(-100%)' : 'translateY(0)',
        opacity: phase === 'entering' ? 0 : (phase === 'leaving' ? 0 : 1),
      }}
      data-testid="pwa-install-banner"
    >
      <div style={styles.badge}>C</div>
      <div style={styles.textWrap}>
        <div style={styles.title}>Installeer CasaOthers</div>
        <div style={styles.subtitle}>Snelle toegang vanaf uw startscherm</div>
      </div>
      <button type="button" onClick={onInstall} style={styles.cta} data-testid="pwa-install-cta">
        Installeer
      </button>
      <button type="button" onClick={onClose} aria-label="Sluiten" style={styles.close} data-testid="pwa-install-close">
        <X size={16} />
      </button>
    </div>
  );
};

const styles = {
  wrap: {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '8px 14px',
    backgroundColor: '#1A1918', color: '#F0EAE0',
    fontFamily: "'Manrope', system-ui, sans-serif",
    fontSize: '13px', fontWeight: 500,
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
    borderBottom: '1px solid #C5A059',
    transition: 'transform 0.35s ease, opacity 0.3s ease',
    pointerEvents: 'auto', // banner is above page but never blocks scrolling below
  },
  badge: {
    width: '28px', height: '28px',
    backgroundColor: '#C5A059', color: '#1A1918',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'Playfair Display', serif",
    fontWeight: 700, fontSize: '14px',
    flexShrink: 0,
  },
  textWrap: { flex: 1, lineHeight: 1.25, minWidth: 0 },
  title: { fontWeight: 600, color: '#F0EAE0' },
  subtitle: { fontSize: '11px', color: '#C5A059', opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  cta: {
    backgroundColor: '#C5A059', color: '#1A1918', border: 'none',
    padding: '6px 12px', fontSize: '11px', fontWeight: 700,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    fontFamily: 'inherit', cursor: 'pointer', flexShrink: 0,
  },
  close: {
    background: 'transparent', border: 'none', color: '#8C8B88',
    padding: '4px', cursor: 'pointer', display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
};

export default InstallBanner;
